import { isPraise } from '@/lib/who-match';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import { whoOf, isSelf } from '@/lib/prayer-utils';
import { useTestimonyEdit } from '@/hooks/useTestimonyEdit';
import { useSaveCue } from '@/hooks/useSaveCue';
import { isCoreReadOnly } from '@/lib/core-cutover';
import MicButton from './MicButton';
import SheetShell from './SheetShell';
import UpdateComposer, { type UpdateComposerHandle } from './UpdateComposer';
import sheetStyles from './UpdateSheet.module.css';
import composerStyles from './UpdateComposer.module.css';

type Variant = 'update' | 'testimony';

interface UpdateSheetProps {
  prayer: Prayer;
  variant?: Variant;
  /** Namespace for voice-target registration. Must be unique per open sheet
   *  (prayer id already included internally). */
  voiceTargetPrefix?: string;
  onClose: () => void;
  /** Coordinator-computed z for this sheet's layer (A1); forwarded to
   *  SheetShell. Undefined for non-migrated callers (CSS fallback). */
  zIndex?: number;
}

/**
 * Shared bottom-sheet composer used by every "add update / edit testimony"
 * surface in the app (Garden vine, Person timeline, Faithfulness Summary,
 * Evidence Book). The slide-up shell + iOS keyboard tracking lives in
 * <SheetShell>; the progress-update form body lives in <UpdateComposer>;
 * the testimony body stays inline below since it has only one consumer
 * (extract-on-second-occurrence).
 *
 * The `variant` switch selects which hook drives the composition:
 * - "update"    → usePrayerUpdate via UpdateComposer (appends a progress
 *                 entry + reparses)
 * - "testimony" → useTestimonyEdit inline (sets prayer.testimony, no
 *                 reparse)
 */
export default function UpdateSheet({
  prayer,
  variant = 'update',
  voiceTargetPrefix,
  onClose,
  zIndex,
}: UpdateSheetProps) {
  // All variants are compose surfaces. Existing prayer content stays visible
  // in its owning screen; the editor itself does not mount during cutover.
  if (isCoreReadOnly()) return null;
  if (variant === 'testimony') {
    return (
      <TestimonySheet
        prayer={prayer}
        voiceTargetPrefix={voiceTargetPrefix}
        onClose={onClose}
        zIndex={zIndex}
      />
    );
  }
  return (
    <ProgressUpdateSheet
      prayer={prayer}
      voiceTargetPrefix={voiceTargetPrefix}
      onClose={onClose}
      zIndex={zIndex}
    />
  );
}

// ---- Progress-update variant ----

function ProgressUpdateSheet({
  prayer,
  voiceTargetPrefix = 'update',
  onClose,
  zIndex,
}: Omit<UpdateSheetProps, 'variant'>) {
  const { t } = useTranslation();
  const composerRef = useRef<UpdateComposerHandle | null>(null);
  const rawWho = whoOf(prayer);
  const displayName = isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho;
  // Show the full prayer text the user is updating, not a short summary.
  // Clamped to a scrollable max-height in CSS so long prayers don't push
  // the mic + textarea + actions off-screen.
  const context = prayer.rawText;

  // Ordinary dismissal keeps the current-document draft. Registration cleanup
  // joins the existing recording stop/store path; a pending write holds Close.
  const handleBackdrop = () => {
    if (composerRef.current?.keepDraft() !== false) onClose();
  };

  return (
    <SheetShell
      ariaLabel={`${t('garden.continue')} — ${displayName}`}
      onBackdrop={handleBackdrop}
      closeLabel={t('summary.close')}
      zIndex={zIndex}
      dragToDismiss
    >
      <div className={sheetStyles.header}>
        <span className={sheetStyles.title}>{t('garden.continue')}</span>
        <span className={sheetStyles.who}>{displayName}</span>
      </div>
      {context && <p className={sheetStyles.context}>{context}</p>}
      <UpdateComposer
        key={prayer.id}
        ref={composerRef}
        prayer={prayer}
        voiceTarget={`${voiceTargetPrefix}-${prayer.id}`}
        onCommit={onClose}
        onClose={onClose}
      />
    </SheetShell>
  );
}

// ---- Testimony variant ----

function TestimonySheet({
  prayer,
  voiceTargetPrefix = 'testimony',
  onClose,
  zIndex,
}: Omit<UpdateSheetProps, 'variant'>) {
  const { t } = useTranslation();
  const editor = useTestimonyEdit({
    prayer,
    voiceTarget: `${voiceTargetPrefix}-${prayer.id}`,
    onSaved: onClose,
    onCancelled: onClose,
  });
  const showSaveCue = useSaveCue(
    editor.isListening || editor.isTranscribing,
    editor.displayValue,
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') editor.cancelEdit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editor]);

  const rawWho = whoOf(prayer);
  const displayName = isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho;
  // Show the full prayer text so the user remembers exactly what they
  // asked for when writing a testimony about how God answered it.
  const context = prayer.rawText;

  return (
    <SheetShell
      ariaLabel={`${t('evidence.sheetLabel')} — ${displayName}`}
      onBackdrop={editor.cancelEdit}
      closeLabel={t('summary.close')}
      zIndex={zIndex}
      dragToDismiss
    >
      <div className={sheetStyles.header}>
        <span className={sheetStyles.title}>{t('evidence.sheetLabel')}</span>
        <span className={sheetStyles.who}>{displayName}</span>
      </div>
      {context && <p className={sheetStyles.context}>{context}</p>}
      <div className={composerStyles.body}>
        <textarea
          className={composerStyles.textarea}
          value={editor.displayValue}
          onChange={editor.handleChange}
          placeholder={t('evidence.testimonyPlaceholder')}
          aria-label={t('evidence.testimonyInputAria')}
          rows={4}
          disabled={editor.isSaving}
        />
        {(editor.saveError || editor.hasFailedRecovery || editor.hasParkedRecovery) && (
          <div className={composerStyles.saveError} role="alert">
            <span>
              {t(
                editor.hasParkedRecovery
                  ? 'detail.recoveryParked'
                  : editor.hasFailedRecovery
                    ? 'detail.recoveryFailed'
                  : editor.saveNeedsReopen
                    ? 'voice.saveNeedsReopen'
                  : 'detail.saveError',
              )}
            </span>
            {editor.canRetryRecovery && (
              <button
                type="button"
                className={composerStyles.retryRecovery}
                onClick={editor.retryRecovery}
                disabled={editor.isSaving}
              >
                {t('detail.retryRecovery')}
              </button>
            )}
          </div>
        )}
        {editor.isSupported && (
          <div className={composerStyles.micArea}>
            <MicButton
              size="lg"
              isListening={editor.isListening}
              isTranscribing={editor.isTranscribing}
              showLabel
              onClick={editor.toggleMic}
              disabled={editor.isSaving}
              ariaLabel={editor.isListening ? t('detail.stopRecording') : t('detail.voiceInput')}
            />
          </div>
        )}
        {/* Whole actions row hidden during recording/transcribing — see
            UpdateComposer for the rationale. */}
        {!editor.isListening && !editor.isTranscribing && (
          <>
            {showSaveCue && (
              <div className={composerStyles.hint} aria-hidden="true">
                {t('evidence.testimonyHint')}
              </div>
            )}
            <div className={composerStyles.actions} data-keyboard-actions="">
              <div className={composerStyles.spacer} />
              <button type="button" className={composerStyles.cancel} onClick={editor.cancelEdit} disabled={editor.isSaving}>
                {t('evidence.skip')}
              </button>
              <button
                type="button"
                className={`${composerStyles.submit} ${showSaveCue ? composerStyles.submitCue : ''}`}
                onClick={editor.commitTestimony}
                disabled={!editor.canSubmit || editor.isSaving}
              >
                {t('evidence.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </SheetShell>
  );
}
